import React, { useState } from 'react';
import { TableCell, Box, IconButton, TableCellProps, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import { DesktopDatePicker } from '@mui/x-date-pickers';

interface EditableDateCellProps extends TableCellProps {
  value: Date | undefined;
  onValueChange: (value: Date | undefined) => void;
  placeholder?: string;
  format?: string;
  asDiv?: boolean;
  minDate?: Date;
  maxDate?: Date;
  prefix?: string;
}

const EditableDateCell: React.FC<EditableDateCellProps> = ({
  value,
  onValueChange,
  placeholder = 'Select date',
  format = 'MMM dd, yyyy',
  asDiv = false,
  sx,
  minDate,
  maxDate,
  prefix,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState<Date | null>(value || null);
  const [showEditIcon, setShowEditIcon] = useState(false);
  


  const setDate = (date: Date | null) => {
    setInputValue(date);
    onValueChange(date || undefined);
    setIsEditing(false);
  }

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleAccept = () => {
    onValueChange(inputValue || undefined);
    setIsEditing(false);
    setShowEditIcon(false);
  };

  const handleDecline = () => {
    setInputValue(value || null);
    setIsEditing(false);
    setShowEditIcon(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAccept();
    } else if (event.key === 'Escape') {
      handleDecline();
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return placeholder;

    if (date.toDateString() === new Date().toDateString()) {
      return 'Today';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const content = (
    <Box
      onClick={handleClick}
      onMouseOver={() => setShowEditIcon(true)}
      onMouseOut={() => setShowEditIcon(false)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.8
        },
        ...sx
      }}
    >
      {prefix && (
        <Typography variant="body2" color="text.secondary">
          {prefix}
        </Typography>
      )}
      <Typography variant="body2" color="text.primary">
        {formatDate(value)}
      </Typography>

      {showEditIcon && <EditIcon fontSize="small" sx={{ opacity: 0.7 }} />}
    </Box>
  );

  const editingContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, position: 'relative' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDatePicker
          value={inputValue}
          onChange={(newValue: Date | null) => setDate(newValue)}
          minDate={minDate}
          maxDate={maxDate}
          showDaysOutsideCurrentMonth
          slotProps={{
            textField: {
              size: 'small',
              placeholder: placeholder,
              onKeyDown: handleKeyPress,
              autoFocus: true
            }
          }}
        />
      </LocalizationProvider>

    </Box>
  );

  if (asDiv) {
    return isEditing ? editingContent : content;
  }

  return (
    <TableCell {...props} sx={sx}>
      {isEditing ? editingContent : content}
    </TableCell>
  );
};

export default EditableDateCell; 